import { BottomSheetStackApi } from "@/components/BottomSheet";
import { AreaStateAggregateType } from "@/routes/area-aggregate/types/area-state-aggregate.type";
import { CommandTrackingView } from "@/types/command-tracking.type";

import AreaLockSheetBody from "../area-lock";
import AtmosphericDataViewSheetBody from "../climate-data/atmospheric-view";
import ClimateHistoryChoiceSheetBody from "../climate-data/climate-history-choice";
import RainfallDataSheetBody from "../climate-data/rainfall-view";
import ModuleAccessSheetBody from "../module-maintenance/module-access";
import ModulesListSheetBody from "../modules-list";
import OopsSheetBody from "../oops";
import ProtectionDetailSheetBody from "../protection-detail";
import ProtectionListSheetBody from "../protection-list";
import ScanModuleSheetBody from "../scan-module";
import SkipActionSheetBody from "../skip-action";
import WeatherForecastSheetBody from "../weather-forecast";
import { SheetsEnum } from "../enums/sheets.enum";
import { SheetsType } from "../types/sheets.type";
import ActionAreaSheetBody from "../area-action/action-area-launcher";
import ActionInProgressSheetBody from "../area-action/action-in-progress";

type SwitchSheetsRenderProps = {
  sheet: BottomSheetStackApi<SheetsType>;
  /**
   * Contextes propres aux sheets liés à une parcelle (`MODULES_LIST`,
   * `MODULE_ACCESS`, `AREA_LOCK`, `ACTION`, `ACTION_IN_PROGRESS`,
   * `SKIP_ACTION`). Optionnels parce que les sheets niveau application
   * (ex. `SCAN_MODULE`) n'en ont pas besoin.
   */
  areaStateAggregate?: AreaStateAggregateType;
  areaCommandTracking?: CommandTrackingView;
};

export function SwitchSheetsRender({
  sheet,
  areaStateAggregate,
  areaCommandTracking,
}: SwitchSheetsRenderProps) {
  switch (sheet.visibleView?.id) {
    case SheetsEnum.SCAN_MODULE:
      return <ScanModuleSheetBody pushSheet={sheet.push} />;
    case SheetsEnum.MODULES_LIST:
      if (!areaStateAggregate) return null;
      return (
        <ModulesListSheetBody
          areaStateAggregate={areaStateAggregate}
          firstOfList={sheet.visibleView.firstOfList}
          pushSheet={sheet.push}
        />
      );
    case SheetsEnum.MODULE_ACCESS:
      return (
        <ModuleAccessSheetBody
          areaId={sheet.visibleView.areaId}
          moduleAddress={sheet.visibleView.moduleAddress}
        />
      );
    case SheetsEnum.AREA_LOCK:
      if (!areaStateAggregate) return null;
      return (
        <AreaLockSheetBody
          areaStateAggregate={areaStateAggregate}
          onDone={sheet.close}
        />
      );
    case SheetsEnum.ACTION:
      if (!areaStateAggregate || !areaCommandTracking) return null;
      return (
        <ActionAreaSheetBody
          action={sheet.visibleView.action}
          areaId={areaStateAggregate.areaId}
          pushSheet={sheet.push}
        />
      );
    case SheetsEnum.ACTION_IN_PROGRESS:
      if (!areaStateAggregate || !areaCommandTracking) return null;
      return (
        <ActionInProgressSheetBody
          pushSheet={sheet.push}
          areaStateAggregate={areaStateAggregate}
          areaCommandTracking={areaCommandTracking}
          context={sheet.visibleView.context}
        />
      );
    case SheetsEnum.SKIP_ACTION:
      if (!areaStateAggregate) return null;
      return (
        <SkipActionSheetBody
          areaId={areaStateAggregate.areaId}
          skippedCommand={sheet.visibleView.context.skippedCommand}
          pushSheet={sheet.push}
          backFn={sheet.pop}
        />
      );
    case SheetsEnum.CLIMATE_RAINFALL_VIEW:
      if (!areaStateAggregate) return null;
      return <RainfallDataSheetBody areaId={areaStateAggregate.areaId} />;
    case SheetsEnum.CLIMATE_ATMOSPHERIC_VIEW:
      if (!areaStateAggregate) return null;
      return (
        <AtmosphericDataViewSheetBody
          areaId={areaStateAggregate.areaId}
          type={sheet.visibleView.type}
        />
      );
    case SheetsEnum.CLIMATE_HISTORY_CHOICE:
      return <ClimateHistoryChoiceSheetBody pushSheet={sheet.push} />;
    case SheetsEnum.WEATHER_FORECAST:
      if (!areaStateAggregate) return null;
      return <WeatherForecastSheetBody areaId={areaStateAggregate.areaId} />;
    case SheetsEnum.PROTECTION_LIST:
      if (!areaStateAggregate) return null;
      return (
        <ProtectionListSheetBody
          areaId={areaStateAggregate.areaId}
          pushSheet={sheet.push}
        />
      );
    case SheetsEnum.PROTECTION_DETAIL:
      if (!areaStateAggregate) return null;
      return (
        <ProtectionDetailSheetBody
          areaId={areaStateAggregate.areaId}
          protectionKey={sheet.visibleView.protectionKey}
        />
      );
    default:
      return <OopsSheetBody />;
  }
}
